import { supabase } from '../lib/supabase';
import toastService from '../components/ui/Toast';

/**
 * Send booking confirmation email
 * @param {Object} params - Email parameters
 * @returns {Promise<Object>} Response from email service
 */
export const sendBookingConfirmation = async ({ recipientEmail, recipientName, bookingData, trailerData, ownerData }) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-booking-notification', {
      body: {
        emailType: 'booking_confirmation',
        recipientEmail,
        recipientName,
        bookingData,
        trailerData,
        ownerData
      }
    });

    if (error) {
      console.error('Error sending booking confirmation:', error);
      return { success: false, error };
    }

    // Show toast notification
    toastService?.bookingConfirmation({
      trailerName: trailerData?.title,
      bookingReference: bookingData?.bookingReference
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error in sendBookingConfirmation:', error);
    return { success: false, error };
  }
};

/**
 * Send check-in reminder email
 * @param {Object} params - Email parameters
 * @returns {Promise<Object>} Response from email service
 */
export const sendCheckInReminder = async ({ recipientEmail, recipientName, bookingData, trailerData, ownerData }) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-booking-notification', {
      body: {
        emailType: 'check_in_reminder',
        recipientEmail,
        recipientName,
        bookingData,
        trailerData,
        ownerData
      }
    });

    if (error) {
      console.error('Error sending check-in reminder:', error);
      return { success: false, error };
    }

    // Show toast notification
    toastService?.checkInReminder({
      startDate: new Date(bookingData?.startDate)?.toLocaleDateString()
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error in sendCheckInReminder:', error);
    return { success: false, error };
  }
};

/**
 * Send return inspection reminder email
 * @param {Object} params - Email parameters
 * @returns {Promise<Object>} Response from email service
 */
export const sendReturnInspectionReminder = async ({ recipientEmail, recipientName, bookingData, trailerData, ownerData }) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-booking-notification', {
      body: {
        emailType: 'return_inspection_reminder',
        recipientEmail,
        recipientName,
        bookingData,
        trailerData,
        ownerData
      }
    });

    if (error) {
      console.error('Error sending return inspection reminder:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in sendReturnInspectionReminder:', error);
    return { success: false, error };
  }
};

/**
 * Send dispute update email
 * @param {Object} params - Email parameters
 * @returns {Promise<Object>} Response from email service
 */
export const sendDisputeUpdate = async ({ recipientEmail, recipientName, bookingData, trailerData }) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-booking-notification', {
      body: {
        emailType: 'dispute_update',
        recipientEmail,
        recipientName,
        bookingData,
        trailerData
      }
    });

    if (error) {
      console.error('Error sending dispute update:', error);
      return { success: false, error };
    }

    // Show toast notification
    toastService?.disputeUpdate({
      message: 'Dispute status has been updated',
      status: bookingData?.disputeStatus
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error in sendDisputeUpdate:', error);
    return { success: false, error };
  }
};

/**
 * Send payout confirmation email
 * @param {Object} params - Email parameters
 * @returns {Promise<Object>} Response from email service
 */
export const sendPayoutConfirmation = async ({ recipientEmail, recipientName, payoutData, bookingData, trailerData }) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-payout-notification', {
      body: {
        recipientEmail,
        recipientName,
        payoutData,
        bookingData,
        trailerData
      }
    });

    if (error) {
      console.error('Error sending payout confirmation:', error);
      return { success: false, error };
    }

    // Show toast notification
    toastService?.payoutConfirmation({
      amount: payoutData?.netAmount,
      transactionId: payoutData?.stripeTransferId
    });

    return { success: true, data };
  } catch (error) {
    console.error('Error in sendPayoutConfirmation:', error);
    return { success: false, error };
  }
};

/**
 * Send booking status update email (accepted or declined) to renter
 * @param {Object} params - Email parameters
 * @returns {Promise<Object>} Response from email service
 */
export const sendBookingStatusUpdate = async ({ recipientEmail, recipientName, emailType, bookingData, trailerData }) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-booking-notification', {
      body: {
        emailType,
        recipientEmail,
        recipientName,
        bookingData,
        trailerData
      }
    });

    if (error) {
      console.error('Error sending booking status update:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in sendBookingStatusUpdate:', error);
    return { success: false, error };
  }
};

/**
 * Send review reminder email to renter 2 days after return inspection completes
 * @param {Object} params - Email parameters
 * @returns {Promise<Object>} Response from email service
 */
export const sendReviewReminder = async ({ recipientEmail, recipientName, bookingData, trailerData }) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-booking-notification', {
      body: {
        emailType: 'review_reminder',
        recipientEmail,
        recipientName,
        bookingData,
        trailerData
      }
    });

    if (error) {
      console.error('Error sending review reminder:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in sendReviewReminder:', error);
    return { success: false, error };
  }
};

const ADMIN_EMAIL = 'admin@trailerhub.com';

/**
 * Send admin notification when a new renter or owner signs up
 */
export const sendAdminSignupNotification = async ({ userId, userName, userEmail, accountType, phone }) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-booking-notification', {
      body: {
        emailType: 'admin_notification_signup',
        recipientEmail: ADMIN_EMAIL,
        recipientName: 'Admin',
        bookingData: {
          userId,
          userName,
          userEmail,
          accountType,
          phone,
          signedUpAt: new Date()?.toISOString()
        }
      }
    });

    if (error) {
      console.error('Error sending admin signup notification:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in sendAdminSignupNotification:', error);
    return { success: false, error };
  }
};

/**
 * Send admin notification when a transaction is completed
 */
export const sendAdminTransactionNotification = async ({ bookingReference, totalAmount, paymentIntentId, renterName, renterEmail, trailerTitle, trailerType, trailerLocation, startDate, endDate }) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('send-booking-notification', {
      body: {
        emailType: 'admin_notification_transaction',
        recipientEmail: ADMIN_EMAIL,
        recipientName: 'Admin',
        bookingData: {
          bookingReference,
          totalAmount,
          paymentIntentId,
          renterName,
          renterEmail,
          startDate,
          endDate,
          transactionDate: new Date()?.toISOString()
        },
        trailerData: {
          title: trailerTitle,
          type: trailerType,
          location: trailerLocation
        }
      }
    });

    if (error) {
      console.error('Error sending admin transaction notification:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in sendAdminTransactionNotification:', error);
    return { success: false, error };
  }
};

export const emailService = {
  sendBookingConfirmation,
  sendCheckInReminder,
  sendReturnInspectionReminder,
  sendDisputeUpdate,
  sendPayoutConfirmation,
  sendBookingStatusUpdate,
  sendAdminSignupNotification,
  sendAdminTransactionNotification,
  sendReviewReminder
};